import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getUserProfile } from '../utils/socialFirestore';
import AbcNotation from '../components/AbcNotation';
import { sanitizeAbcBody } from '../components/sanitizeAbcBody';
import PageWrapperAuto from '../components/PageWrapperAuto';

export default function PublicTuneDetailPage() {
  const { uid, tuneId } = useParams();
  const { user } = useAuth();
  const { textStyle } = useUI();
  const navigate = useNavigate();

  const [tune, setTune] = useState(null);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTune(); }, [uid, tuneId]);

  async function loadTune() {
    try {
      const [tuneSnap, profile] = await Promise.all([
        getDoc(doc(db, 'users', uid, 'tunes', tuneId)),
        getUserProfile(uid)
      ]);
      if (tuneSnap.exists()) {
        setTune({ id: tuneSnap.id, ...tuneSnap.data() });
      } else {
        setError('Tune not found.');
      }
      setOwnerProfile(profile);
    } catch (err) {
      setError('Unable to load this tune. It may be private.');
    }
    setLoading(false);
  }

  async function handleImport() {
    if (!user || !tune) return;
    setImporting(true);
    try {
      const sourceName = ownerProfile?.displayName || ownerProfile?.username || 'another musician';
      const sourceUsername = ownerProfile?.username ? `@${ownerProfile.username}` : '';
      const sourceLabel = sourceUsername ? `${sourceName} (${sourceUsername})` : sourceName;

      const newTuneId = crypto.randomUUID();
      await setDoc(doc(db, 'users', user.uid, 'tunes', newTuneId), {
        id: newTuneId,
        title: tune.title || '', type: tune.type || '', key: tune.key || '',
        genre: tune.genre || '', composer: tune.composer || '',
        abc: tune.abc || '', lyrics: tune.lyrics || '', chords: tune.chords || '',
        notes: '', link1: tune.link1 || '', link2: tune.link2 || '', link3: tune.link3 || '',
        customTags: [], source: `Imported from ${sourceLabel}`,
        importedFrom: { uid, tuneId, displayName: sourceName, username: ownerProfile?.username || '' },
        status: '', rating: '', sessions: [], sets: [], recordings: [], attachments: [],
        createdAt: serverTimestamp()
      });
      setImported(true);
    } catch (err) {
      alert('Failed to import tune. Please try again.');
    }
    setImporting(false);
  }

  if (loading) return <PageWrapperAuto><p style={{ textAlign: 'center' }}>Loading...</p></PageWrapperAuto>;

  if (error || !tune) {
    return (
      <PageWrapperAuto>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={textStyle}>{error || 'Tune not found.'}</p>
          <button className="btn-subtle" onClick={() => navigate(-1)} style={{ marginTop: '1rem' }}>
            ← Back
          </button>
        </div>
      </PageWrapperAuto>
    );
  }

  const isOwnTune = user?.uid === uid;

  return (
    <PageWrapperAuto>
      <div style={{ maxWidth: '500px', margin: '0 auto', padding: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <button className="btn-subtle" onClick={() => navigate(-1)}>← Back</button>
          {!isOwnTune && user && (
            <button
              className="btn-subtle"
              onClick={handleImport}
              disabled={importing || imported}
              style={{ padding: '8px 20px' }}
            >
              {imported ? 'Imported!' : importing ? 'Importing...' : 'Import to My Tunes'}
            </button>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
          <h3 style={{ marginBottom: '0.25rem' }}>{tune.title}</h3>

          <p style={{ margin: '2px 0' }}><strong>Genre:</strong> {tune.genre}</p>
          <p style={{ margin: '2px 0' }}><strong>Type:</strong> {tune.type}</p>
          {tune.key && <p style={{ margin: '2px 0' }}><strong>Key:</strong> {tune.key}</p>}
          {tune.composer && <p style={{ margin: '2px 0' }}><strong>Composer:</strong> {tune.composer}</p>}
          {tune.source && <p style={{ margin: '2px 0' }}><strong>Source:</strong> {tune.source}</p>}
        </div>

        {/* Lyrics */}
        {tune.lyrics && (
          <details open style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Lyrics</summary>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{tune.lyrics}</div>
          </details>
        )}

        {/* Chords */}
        {tune.chords && (
          <details style={{ marginBottom: '0.5rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Chords</summary>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{tune.chords}</div>
          </details>
        )}

        {/* ABC Notation */}
        {tune.abc && (
          <>
            <details open style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>ABC Notation</summary>
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <div style={{
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '8px',
                  padding: '8px',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  margin: '0 auto'
                }}>
                  <AbcNotation abc={tune.abc} />
                </div>
              </div>
            </details>

            <details style={{ marginTop: '0.25rem', marginBottom: '0.5rem' }}>
              <summary style={{ cursor: 'pointer', fontSize: '0.9rem' }}>Show Raw ABC Code</summary>
              <pre style={{ whiteSpace: 'pre-wrap', marginTop: '0.5rem' }}>{sanitizeAbcBody(tune.abc)}</pre>
            </details>
          </>
        )}

        {/* Links */}
        {[tune.link1, tune.link2, tune.link3].some(Boolean) && (
          <>
            <p style={{ margin: '0.5rem 0 0.25rem' }}><strong>Links:</strong></p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              {[tune.link1, tune.link2, tune.link3].filter(Boolean).map((link, i) => (
                <a
                  key={i}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    backgroundColor: 'var(--input-bg)',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    textDecoration: 'underline',
                    wordBreak: 'break-all',
                    fontSize: '0.85rem'
                  }}
                >
                  {link}
                </a>
              ))}
            </div>
          </>
        )}

        {/* Import button at bottom too for long tunes */}
        {!isOwnTune && user && !imported && (
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <button
              className="btn-subtle"
              onClick={handleImport}
              disabled={importing}
              style={{ padding: '10px 24px' }}
            >
              {importing ? 'Importing...' : 'Import to My Tunes'}
            </button>
          </div>
        )}

        {imported && (
          <p style={{ ...textStyle, textAlign: 'center', marginTop: '1rem', color: '#2a2' }}>
            Tune imported! You can find it in your tunes.
          </p>
        )}
      </div>
    </PageWrapperAuto>
  );
}
